import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function SpaendingerPage() {
  const symptomPoints = [
    {
      title: "Ømhed øverst på skuldrene",
      desc: "Efter en lang arbejdsdag foran computerskærmen, hvor trapezius og skuldre har været spændt og løftet.",
      icon: "fa-solid fa-laptop",
    },
    {
      title: "Stivhed ved bevægelse",
      desc: "Nedsat bevægelighed og ubehag, når du drejer hovedet eller skal kigge over skulderen under kørsel.",
      icon: "fa-solid fa-arrows-rotate",
    },
    {
      title: "Træthed i nakken",
      desc: "Muskeltræthed og tyngdefornemmelse ved længere tids arbejde eller fastholdt stilling i hverdagen.",
      icon: "fa-solid fa-battery-quarter",
    },
    {
      title: "Hovedpine med spændinger",
      desc: "Trykkende smerter, som optræder sammen med nakkeproblemerne og trækker op mod tindinger eller pande.",
      icon: "fa-solid fa-head-side-virus",
    },
  ];

  const treatmentSteps = [
    {
      number: "01",
      title: "Grundig indledende samtale",
      desc: "Vi afdækker dine gener, hvor du har ondt, og hvordan din hverdag former sig, før vi beslutter behandlingen.",
    },
    {
      number: "02",
      title: "Japansk puls- og mavetest",
      desc: "Traditionel vurdering i Keiraku Chiryo, som identificerer kroppens meridianer og energetiske ubalancer.",
    },
    {
      number: "03",
      title: "Nænsom nålebehandling",
      desc: "Ultra-tynde nåle på 0,12 mm eller ikke-penetrerende kontaktnåle tilpasset din personlige følsomhed.",
    },
    {
      number: "04",
      title: "Løbende opfølgning",
      desc: "Vi evaluerer effekten i din hverdag, ser på ændringer og justerer forløbet, så det giver reel mening for dig.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Akupunktur mod Spændinger | Risskov Klinik</title>
        <meta
          name="description"
          content="Slip af med fastlåste spændinger i nakke, skuldre og ryg. Nænsom japansk akupunktur i Risskov giver dyb afspænding og smertelindring. Bestil tid."
        />
        <meta
          property="og:title"
          content="Akupunktur mod Spændinger | Risskov Klinik"
        />
        <meta
          property="og:description"
          content="Slip af med fastlåste spændinger i nakke, skuldre og ryg. Nænsom japansk akupunktur i Risskov giver dyb afspænding og smertelindring. Bestil tid."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/spaendinger" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Akupunktur mod muskelspændinger i Risskov",
            "url": "https://kita-akupunktur.dk/spaendinger",
            "description":
              "Nænsom japansk akupunktur mod spændinger i nakke og skulder i Risskov nær Aarhus, også for klienter fra Jystrup.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Kita Akupunktur",
              "telephone": "+4529387186",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={1} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummesti" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-[#7a5b4f]">
              <li>
                <Link to="/" className="hover:text-[#d74814] transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li>
                <Link to="/akupunktur" className="hover:text-[#d74814] transition-colors">
                  Akupunktur
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li className="text-[#d74814] font-medium" aria-current="page">
                Muskelspændinger
              </li>
            </ol>
          </nav>

          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>FOKUSOMRÅDE • MUSKELAFSPÆNDING & RO</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427] max-w-4xl leading-tight">
            Akupunktur mod muskelspændinger i nakke og skuldre i Risskov
          </h1>

          <p className="text-base sm:text-lg text-[#7a5b4f] mt-4 max-w-3xl leading-relaxed">
            Gør en øm nakke eller tunge skuldre det svært at finde ro ved skrivebordet eller om aftenen? Hos Kita Akupunktur i Risskov tilbyder jeg japansk akupunktur ved spændinger i nakke og skulder, også til dig fra Jystrup.
          </p>

          {/* Quick Info Badges */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
            >
              <i className="fa-regular fa-calendar-check text-xs" aria-hidden="true" />
              <span>Bestil tid til muskelspændinger</span>
            </Link>

            <a
              href="tel:+4529387186"
              className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white/90 text-[#593427] text-sm font-medium hover:bg-white border border-[#ffe0cf] transition-colors shadow-2xs"
            >
              <i className="fa-solid fa-phone text-[#d74814] text-xs" aria-hidden="true" />
              <span>Tlf. 29 38 71 86</span>
            </a>

            <div className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-[#e1ef9f]/30 border border-[#e1ef9f] text-xs text-[#593427]">
              <i className="fa-solid fa-square-parking text-[#593427]" aria-hidden="true" />
              <span>Gratis parkering ved klinikken</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 sm:py-20 bg-[#fffaf7] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

          {/* Section 1: Introduktion & Tilpasning */}
          <div className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden">
            <div className="max-w-4xl space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>INDIVIDUELT TILPASSET BEHANDLING</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#593427] leading-snug">
                Spændinger i nakke og skulder, også for dig fra Jystrup
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
                <p>
                  Gør en øm nakke eller tunge skuldre det svært at finde ro ved skrivebordet eller om aftenen? Hos{" "}
                  <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
                    Kita Akupunktur
                  </Link>{" "}
                  i Risskov tilbyder jeg japansk akupunktur ved spændinger i nakke og skulder, også til dig fra Jystrup. Jeg tager udgangspunkt i dine gener og tilpasser behandlingen til dig, din hverdag og din følsomhed over for nåle.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Hvorfor opstår spændinger i nakke og skuldre? */}
          <div className="space-y-8">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>ÅRSAGER & FORSTÅELSE</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Hvorfor opstår spændinger i nakke og skuldre?
              </h2>
              <div className="space-y-4 text-[#7a5b4f] text-base sm:text-lg leading-relaxed">
                <p>
                  Spændinger i nakke kan hænge sammen med flere ting. Lange perioder i samme stilling, gentagne bevægelser og en travl hverdag kan spille ind. Det er ikke altid én bestemt arbejdsstilling, der er problemet. Derfor taler vi både om dine symptomer og om, hvordan du bruger kroppen i løbet af dagen.
                </p>
                <p>
                  Stress og dårlig søvn kan også have betydning for, hvordan smerter opleves. Måske opdager du først sidst på dagen, at skuldrene har været løftet i flere timer. Når du fortæller om dine gener, er det nyttigt at lægge mærke til, hvornår de kommer, og hvad der gør dem bedre eller værre. Det kan eksempelvis være følgende:
                </p>
              </div>
            </div>

            {/* 4 Symptoms Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {symptomPoints.map((sym, idx) => (
                <div
                  key={idx}
                  className="bg-white/95 p-6 rounded-2xl border border-[#ffe0cf] hover:border-[#d74814]/40 hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="w-11 h-11 rounded-xl bg-[#ffe0cf]/50 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                      <i className={`${sym.icon} text-lg`} aria-hidden="true" />
                    </div>
                    <h3 className="font-serif font-semibold text-base text-[#593427]">
                      {sym.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                      {sym.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Link box: Nakkegener og hovedpine */}
            <div className="bg-[#fff5ee] p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] space-y-3">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <i className="fa-solid fa-head-side-medical text-[#d74814] text-xl mt-1 shrink-0" aria-hidden="true" />
                <div className="space-y-3">
                  <h3 className="font-serif font-semibold text-lg text-[#593427]">
                    Nakkegener og hovedpine kan optræde samtidig
                  </h3>
                  <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                    Nakkegener og hovedpine kan optræde samtidig, men hovedpine skyldes ikke altid spændte muskler. Jeg spørger derfor ind til begge dele, hvis du oplever den kombination. Du kan læse mere om mit tilbud ved hovedpine og migræne på den særskilte side.
                  </p>
                  <div>
                    <Link
                      to="/migraene"
                      className="inline-flex items-center space-x-2 text-xs sm:text-sm font-semibold text-[#d74814] hover:underline"
                    >
                      <span>Læs mere om mit tilbud ved hovedpine og migræne</span>
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Akupunktur mod smerter & Japansk metode */}
          <div className="space-y-6">
            <div className="max-w-3xl space-y-2">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>METODE & NÆNSOMHED</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Akupunktur mod smerter for dig fra Aarhus og Jystrup
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/95 p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-xs space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-[#ffe0cf]/50 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                  <i className="fa-solid fa-hand-holding-heart text-xl" aria-hidden="true" />
                </div>
                <h3 className="font-serif font-semibold text-xl text-[#593427]">
                  Lindring som et trygt supplement
                </h3>
                <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                  Søger du akupunktur mod smerter i Aarhus, eller kommer du fra Jystrup, er min klinik i Risskov en mulighed. Her tilbyder jeg individuelt tilpasset behandling med lindring som formål. Akupunktur kan indgå som et supplement til andre indsatser, men jeg kan ikke love en bestemt effekt eller love, at behandlingen fjerner årsagen til dine gener.
                </p>
              </div>

              <div className="bg-white/95 p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-xs space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-[#e1ef9f]/40 border border-[#e1ef9f] flex items-center justify-center text-[#593427]">
                  <i className="fa-solid fa-feather-pointed text-xl" aria-hidden="true" />
                </div>
                <h3 className="font-serif font-semibold text-xl text-[#593427]">
                  Japansk præcision og 0,12 mm nåle
                </h3>
                <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                  Mit særlige fokus er japansk akupunktur. Jeg bruger meget tynde, sterile engangsnåle, og ved den japanske metode anvender jeg blandt andet nåle på 0,12 mm. Teknik og indstiksdybde tilpasses din sensitivitet. Det giver os mulighed for at tage hensyn til, hvordan du oplever behandlingen undervejs.
                </p>
              </div>
            </div>
          </div>

          {/* Section 4: Er du utryg ved akupunkturnåle? & Et forløb med plads til dine spørgsmål */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-6 bg-gradient-to-br from-white via-[#fffaf7] to-[#fff5ee] p-7 sm:p-9 rounded-3xl border border-[#ffe0cf] shadow-xs space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>NÅLEFØLSOMHED & TRYGHED</span>
              </div>
              <h3 className="text-2xl font-serif text-[#593427]">
                Er du utryg ved akupunkturnåle?
              </h3>
              <div className="space-y-3 text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
                <p>
                  Fortæl mig gerne på forhånd, hvis du er nervøs for nåle eller reagerer kraftigt på berøring. Vi taler om mulighederne, før behandlingen begynder.
                </p>
                <p>
                  Jeg kan også anvende kontaktnåle, som berører huden uden at trænge ind. Oplevelsen er individuel, og du må altid sige til, hvis noget føles ubehageligt.
                </p>
              </div>
              <div className="pt-2">
                <div className="p-3.5 rounded-xl bg-white border border-[#ffe0cf] flex items-center space-x-3 text-xs sm:text-sm text-[#593427]">
                  <i className="fa-solid fa-shield-heart text-[#d74814] text-lg shrink-0" aria-hidden="true" />
                  <span>Hos mig har du altid fuld kontrol over behandlingen og dit velbefindende.</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 bg-white/95 p-7 sm:p-9 rounded-3xl border border-[#ffe0cf] shadow-xs space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>KONSULTATIONEN</span>
              </div>
              <h3 className="text-2xl font-serif text-[#593427]">
                Et forløb med plads til dine spørgsmål
              </h3>
              <div className="space-y-3 text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
                <p>
                  Ved første besøg taler vi om, hvor du har ondt, hvor længe generne har stået på, og hvad du allerede har prøvet.
                </p>
                <p>
                  Jeg spørger også til blandt andet søvn, belastninger og eventuel anden behandling. Sammen afklarer vi dine ønsker, så forløbet tager udgangspunkt i det, der betyder mest for dig i hverdagen.
                </p>
              </div>
              <div className="pt-2">
                <div className="p-3.5 rounded-xl bg-[#e1ef9f]/20 border border-[#e1ef9f] flex items-center space-x-3 text-xs sm:text-sm text-[#593427]">
                  <JapaneseBadge className="shrink-0" />
                  <span>RAB-registreret akupunktør med god tid og ro til samtale.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Hvor mange behandlinger skal der til? & Lægelig vurdering */}
          <div className="space-y-8">
            <div className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-xs space-y-5">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FORLØB & EVALUERING</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Hvor mange behandlinger skal der til?
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
                <p>
                  Der er ikke ét antal behandlinger, som passer til alle med spændinger i nakke og skulder. Vi følger op på din oplevelse og på, om noget ændrer sig i hverdagen. Det kan være, hvor længe du kan sidde ved computeren, eller hvor let du drejer hovedet. Ud fra det vurderer vi, om forløbet skal justeres eller afsluttes.
                </p>
              </div>
            </div>

            {/* Sikkerhedsboks: Akut lægehjælp & undersøgelse */}
            <div className="p-6 sm:p-8 rounded-3xl bg-[#ffe0cf]/30 border-2 border-[#ffe0cf] space-y-4">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#d74814] shrink-0 border border-[#ffe0cf]">
                  <i className="fa-solid fa-triangle-exclamation text-lg" aria-hidden="true" />
                </div>
                <div className="space-y-2.5">
                  <h3 className="font-serif font-bold text-lg text-[#593427]">
                    Lægelig vurdering og sikkerhed
                  </h3>
                  <div className="space-y-2 text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                    <p>
                      Ved nye eller ændrede symptomer kan en lægelig vurdering være nødvendig frem for flere behandlinger. Søg akut lægehjælp ved nakkestivhed sammen med feber eller pludseligt tab af kraft. Føleforstyrrelser og smerter efter et fald bør også vurderes af læge. Akupunktur erstatter ikke relevant undersøgelse og behandling.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Treatment Steps Grid */}
          <div className="glass-sakura p-8 sm:p-12 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>TRYGHED & PROFESSIONALISME</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Sådan forløber dit besøg hos mig
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f] mt-2">
                Hos Kita Akupunktur er ro, nærvær og din personlige sensitivitet i centrum. Du behøver ikke have erfaring med akupunktur på forhånd.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {treatmentSteps.map((step) => (
                <div
                  key={step.number}
                  className="bg-white/85 p-6 rounded-2xl border border-[#ffe0cf] relative flex flex-col justify-between"
                >
                  <div className="space-y-2">
                    <div className="text-3xl font-serif font-bold text-[#d74814]/30">
                      {step.number}
                    </div>
                    <h3 className="font-serif font-semibold text-base text-[#593427]">
                      {step.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 6: Få hjælp til spændinger i nakke og skulder & Om mig */}
          <div className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>RAB-REGISTRERET AKUPUNKTØR</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                  Få hjælp til spændinger i nakke og skulder
                </h2>
                <div className="space-y-3 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
                  <p>
                    Jeg hedder Mayumi Kita Thiele og er registreret alternativ behandler, forkortet RAB, inden for akupunktur. Hos mig møder du en personlig tilgang med tid til samtale og spørgsmål. På siden om Kita Akupunktur kan du læse mere om min baggrund og mit arbejde med japansk akupunktur.
                  </p>
                  <p>
                    Klinikken ligger på Kantorparken 11, 8240 Risskov, og der er gratis parkering foran huset. Søger du behandling for spændinger i nakke og skulder i Aarhus, eller kommer du fra Jystrup, er du velkommen til at ringe på 29 38 71 86 eller kontakte mig om en tid. Du kan også spørge ind til behandlingen, før du beslutter dig.
                  </p>
                </div>
                <div className="pt-2 flex flex-wrap gap-3">
                  <Link
                    to="/om-mig"
                    className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-[#fff5ee] text-[#d74814] font-medium text-sm border border-[#ffe0cf] hover:bg-[#ffe0cf]/50 transition-colors"
                  >
                    <span>Læs mere om min baggrund og uddannelse</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                  <a
                    href="tel:+4529387186"
                    className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white text-[#593427] font-medium text-sm border border-[#ffe0cf] hover:border-[#d74814] transition-colors"
                  >
                    <i className="fa-solid fa-phone text-[#d74814] text-xs" aria-hidden="true" />
                    <span>Ring 29 38 71 86</span>
                  </a>
                </div>
              </div>

              <div className="lg:col-span-4 bg-[#fff5ee] p-6 rounded-2xl border border-[#ffe0cf] space-y-4 text-center sm:text-left">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-[#d74814] mx-auto sm:mx-0 shadow-2xs">
                  <i className="fa-solid fa-location-dot text-xl" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-[#593427] text-base">
                    Kita Akupunktur i Risskov
                  </h4>
                  <p className="text-xs sm:text-sm text-[#7a5b4f] mt-1 leading-relaxed">
                    Kantorparken 11, 8240 Risskov
                    <br />
                    Tæt på Aarhus N med nem adgang og gratis parkering ved døren. Velkommen til dig fra Jystrup og hele Østjylland.
                  </p>
                </div>
                <div className="pt-1">
                  <div className="inline-flex items-center space-x-1.5 text-xs text-[#d74814] font-medium bg-white px-3 py-1.5 rounded-lg border border-[#ffe0cf]">
                    <i className="fa-solid fa-square-parking" aria-hidden="true" />
                    <span>Gratis parkering foran huset</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related pages links */}
          <div className="p-6 rounded-2xl bg-[#fff5ee] border border-[#ffe0cf] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="space-y-1 text-center sm:text-left">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
                Relaterede behandlingsområder
              </div>
              <p className="text-sm text-[#593427] font-serif">
                Hænger dine spændinger sammen med hovedpine, ryggen, stress eller kæben?
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <Link
                to="/migraene"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Migræne & hovedpine →
              </Link>
              <Link
                to="/stress"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Stress & uro →
              </Link>
              <Link
                to="/ondt-i-ryggen"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Rygsmerter →
              </Link>
              <Link
                to="/ondt-i-kaeben"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Kæbesmerter →
              </Link>
              <Link
                to="/led-og-muskelsmerter"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Led- og muskelsmerter →
              </Link>
              <Link
                to="/japansk-akupunktur"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Japansk akupunktur →
              </Link>
            </div>
          </div>

          {/* Hurtige genveje */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-4 text-xs sm:text-sm">
            <Link
              to="/priser"
              className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
            >
              Se priser & tilskud fra “danmark”
            </Link>
            <Link
              to="/om-mig"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Læs om Mayumi Kita Thiele
            </Link>
            <Link
              to="/faq"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Samlet FAQ
            </Link>
            <Link
              to="/kontakt"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Bestil tid i Risskov
            </Link>
          </div>

          {/* Contact and Booking Section */}
          <div className="pt-6">
            <div className="text-center max-w-2xl mx-auto mb-8 space-y-2">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Bestil tid til akupunktur mod muskelspændinger
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f]">
                Klinikken ligger i fredelige omgivelser på Kantorparken 11 i Risskov med gratis parkering lige ved døren.
              </p>
            </div>
            <ContactForm />
          </div>

          {/* FAQ Section sidst på siden */}
          <div className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
            <FaqSection
              title="Ofte stillede spørgsmål om muskelspændinger"
              subtitle="Få svar på hvordan nænsom akupunktur løsner fastlåste myoser i nakke og skuldre, forbedrer blodcirkulationen og lindrer spændingshovedpine."
              faqs={[
                {
                  question: "Hvorfor opstår der kroniske spændinger og myoser i nakke og skuldre?",
                  answer: (
                    <>
                      Myoser og muskelinfiltrationer opstår typisk som følge af ensidigt skærmarbejde, statisk siddestilling eller langvarig stress, hvor skuldrene ubevidst trækkes op mod ørerne. Dette fører til nedsat blodcirkulation, lokal iltmangel og ophobning af mælkesyre i muskelfibrene. Når musklen låser sig i denne krampelignende sammentrækning, dannes der ømme triggerpunkter, som ofte stråler op i baghovedet som{" "}
                      <Link to="/migraene" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        spændingshovedpine
                      </Link>{" "}
                      eller forplanter sig som{" "}
                      <Link to="/ondt-i-kaeben" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        kæbespændinger
                      </Link>
                      .
                    </>
                  ),
                },
                {
                  question: "Hvordan hjælper akupunktur med at løsne fastlåste muskelknuder?",
                  answer:
                    "Når de tynde akupunkturnåle placeres præcist i eller omkring de spændte triggerpunkter, udløses en lokal refleks i muskelvævet, som får fibrene til at give slip. Nålen stimulerer en markant stigning i blodgennemstrømningen, så ilt og næringsstoffer skylles ind i vævet, mens affaldsstoffer bortledes. Samtidig frigiver centralnervesystemet smertelindrende endorfiner, hvilket bryder smertens onde cirkel.",
                },
                {
                  question: "Hvor hurtigt kan jeg forvente, at nakken og skuldrene slipper spændingerne?",
                  answer:
                    "Mange klienter oplever en markant følelse af lethed, større bevægelighed i nakken og færre spændinger allerede under eller umiddelbart efter den første behandling. Ved genstridige infiltrationer, der har opbygget sig gennem måneder eller år, kræver det typisk 3-5 ugentlige sessioner for at opnå en dybdegående, varig afspænding og forhindre, at musklerne trækker sig sammen igen.",
                },
                {
                  question: "Er akupunktur anderledes eller mere effektivt end massage mod myoser?",
                  answer:
                    "Hvor massage bearbejder vævet udefra gennem hud og overfladiske muskellag, kan akupunktur nå de dybe muskellag med millimeters præcision uden at belaste vævet med hårdt tryk. Mange, der finder hård massage ubehagelig eller oplever, at infiltrationerne vender tilbage få dage efter, opnår langt bedre og mere vedvarende resultater med akupunktur, fordi den nulstiller nervesystemets signalering til musklen.",
                },
                {
                  question: "Hvad kan jeg selv gøre efter behandlingen for at bevare resultatet?",
                  answer: (
                    <>
                      Efter en session er det vigtigt at drikke rigeligt med vand, så kroppen let kan udskille de frigivne affaldsstoffer. Hold nakke og skuldre behageligt varme, og undgå kold træk og hård styrketræning samme dag. I klinikken vejleder jeg dig også gerne i forebyggelse, og du kan læse mere om tilskud fra Sygeforsikring “danmark” under{" "}
                      <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        priser og tilskud
                      </Link>
                      .
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </section>
    </>
  );
}
